<template>
  <div
    class="fixed z-[9999] right-4 bottom-4 sm:right-6 sm:bottom-6"
    style="
      right: max(1rem, env(safe-area-inset-right));
      bottom: max(1rem, env(safe-area-inset-bottom));
    "
  >
    <!-- Proactive pill notification (logic-sheet triggered, shown before the chat is opened) -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 translate-x-2"
      enter-to-class="opacity-100 translate-x-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <button
        v-if="proactiveNotice && !isOpen"
        @click="openFromNotice"
        class="absolute right-16 sm:right-[7.25rem] bottom-7 sm:bottom-32 flex items-center bg-blues rounded-full shadow-xl pl-4 pr-5 py-2 max-w-[240px] hover:shadow-2xl transition-shadow"
      >
        <span
          class="text-[12.5px] font-medium text-white text-left leading-snug truncate"
        >
          {{ proactiveNotice.preview }}
        </span>
        <span
          @click.stop="dismissNotice"
          class="shrink-0 w-5 h-5 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center ml-2"
          aria-label="Dismiss notification"
        >
          <svg viewBox="0 0 24 24" fill="none" class="w-3 h-3 text-slate-500">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <!-- tail: bigger + pushed further right so it tucks under/against the launcher -->
        <span
          class="absolute top-1/2 -right-1 -translate-y-1/2 w-4 h-4 bg-blues rotate-45"
        ></span>
      </button>
    </Transition>

    <!-- Launcher button -->
  <button
  v-if="!isOpen || !isFullscreen"
  @click="toggleOpen"
  class="relative flex items-center justify-center transition-all duration-200 active:scale-95"
  :class="
    isOpen
      ? 'w-16 h-16 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-xl hover:shadow-2xl hover:scale-105'
      : 'w-28 h-36 sm:w-36 sm:h-44 bg-transparent hover:scale-105'
  "
  aria-label="Open chat assistant"
>
  <img
    v-if="!isOpen"
    :key="'launcher-face-' + moodPopKey"
    :src="robotMascotImg"
    alt="Open chat assistant"
    draggable="false"
    class="relative w-24 h-[10rem] sm:w-32 sm:h-[12rem] object-contain pointer-events-none select-none robot-mascot-float robot-3d-tilt motion-reduce:animate-none"
  />

  <svg
    v-else
    viewBox="0 0 24 24"
    fill="none"
    class="w-6 h-6 sm:w-7 sm:h-7 text-white"
  >
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
    />
  </svg>
</button>

    <!-- Chat panel -->
    <Transition
      enter-active-class="animate-[popIn_0.22s_ease-out]"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="flex flex-col overflow-hidden bg-white border border-indigo-100 shadow-2xl fixed inset-0 w-full rounded-none sm:absolute sm:inset-auto sm:bottom-20 sm:right-0 sm:w-[380px] sm:max-w-[380px] sm:h-[70vh] sm:max-h-[600px] sm:min-h-[420px] sm:rounded-2xl sm:origin-bottom-right"
        style="height: 100dvh"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-500 shrink-0"
          style="padding-top: max(0.875rem, env(safe-area-inset-top))"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="w-10 h-10 shrink-0 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-2 ring-white/30"
            >
              <img
                :key="'header-face-' + moodPopKey"
                :src="robotMascotImg"
                alt=""
                draggable="false"
                class="w-7 h-7 object-contain object-top select-none [transform-origin:50%_55%] [will-change:transform] animate-[botIdleShake_4s_ease-in-out_infinite,botPop_0.4s_ease-out_1] motion-reduce:animate-none"
              />
            </div>
            <div class="min-w-0">
              <p
                class="text-white font-semibold text-[15px] leading-tight truncate"
              >
                AI Learning Assistant
              </p>
              <p
                class="flex items-center gap-1.5 text-indigo-100 text-xs mt-0.5"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block shrink-0"
                ></span>
                Online
              </p>
            </div>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button
              @click="resetChat"
              class="w-8 h-8 flex items-center justify-center rounded-full text-white/90 hover:bg-white/15 transition-colors"
              aria-label="Restart chat"
            >
              <svg viewBox="0 0 24 24" fill="none" class="w-4 h-4">
                <path
                  d="M3 12a9 9 0 1 1 3 6.7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <path
                  d="M3 16v-4h4"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              @click="closeChat"
              class="w-8 h-8 flex items-center justify-center rounded-full text-white/90 hover:bg-white/15 transition-colors"
              aria-label="Close chat"
            >
              <svg viewBox="0 0 24 24" fill="none" class="w-4 h-4">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div
          ref="messagesEl"
          class="flex-1 overflow-y-auto px-3.5 sm:px-4 py-4 space-y-4 bg-slate-50"
        >
          <template v-for="m in messages" :key="m.id">
            <!-- Bot message -->
            <div
              v-if="m.from === 'bot'"
              class="flex items-start gap-2.5 animate-[fadeUp_0.2s_ease-out]"
            >
              <div
                class="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mt-0.5"
              >
                <img
                  :src="robotMascotImg"
                  alt=""
                  draggable="false"
                  class="w-6 h-6 object-contain object-top select-none"
                />
              </div>
              <div class="max-w-[85%] sm:max-w-[80%] min-w-0">
                <div
                  class="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md"
                  :class="{
                    'ring-1 ring-emerald-300 bg-emerald-50':
                      m.kind === 'action',
                  }"
                >
                  <p
                    v-if="m.kind === 'action'"
                    class="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 mb-1"
                  >
                    Action triggered
                  </p>
                  <p
                    v-if="m.text"
                    class="text-[13.5px] leading-relaxed text-slate-700 whitespace-pre-line break-words"
                  >
                    {{ m.text }}
                    <span
                      v-if="m.tag === 'course'"
                      class="inline-block align-middle ml-1.5 text-[9.5px] font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
                      >COURSE SIGNAL</span
                    >
                    <span
                      v-else-if="m.tag === 'general'"
                      class="inline-block align-middle ml-1.5 text-[9.5px] font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700"
                      >OFF-TOPIC</span
                    >
                  </p>

                  <!-- NEW: Semester Selection -->
                  <div
                    v-if="m.kind === 'semester_selection'"
                    class="mt-3 flex flex-col gap-2 w-full"
                  >
                    <button
                      v-for="sem in m.semesters"
                      :key="'sem_' + sem.id"
                      @click="selectSemesterForCourses(sem)"
                      class="text-left w-full px-3 py-2.5 bg-[#eef2fe] hover:bg-[#e4ebfe] rounded-lg border border-[#dbe4fe] transition-colors flex flex-col gap-1.5 shadow-sm"
                    >
                      <div class="flex justify-between items-center w-full">
                        <span
                          class="font-bold text-[#3741d4] text-sm truncate pr-2"
                          >{{ sem.semester }}</span
                        >
                        <span
                          v-if="sem.department"
                          class="text-[9px] text-[#636ae3] font-semibold tracking-wide uppercase leading-none shrink-0"
                          >{{ sem.department.name }}</span
                        >
                      </div>
                      <div class="flex w-full justify-between items-end">
                        <span
                          class="text-[10px] text-[#717af0] font-medium leading-none"
                          >Semester</span
                        >
                        <span
                          class="text-[9px] font-bold text-white bg-[#575dec] px-2.5 py-[3px] rounded-md shadow-sm leading-none flex items-center shrink-0"
                          >{{ sem.courseCount }} Courses</span
                        >
                      </div>
                    </button>
                    <p
                      v-if="!m.semesters || m.semesters.length === 0"
                      class="text-xs text-slate-500 italic"
                    >
                      No semesters found.
                    </p>
                  </div>

                  <!-- NEW: Course Selection -->
                  <div
                    v-if="m.kind === 'course_selection'"
                    class="mt-2 flex flex-col gap-1.5 w-full"
                  >
                    <button
                      v-for="course in m.courses"
                      :key="course.id"
                      @click="selectCourseForAssignments(course)"
                      class="text-left w-full px-2.5 py-1.5 bg-[#eef2fe] hover:bg-[#e4ebfe] rounded-lg border border-[#dbe4fe] transition-colors flex justify-between items-center shadow-sm"
                    >
                      <span
                        class="font-bold text-[#3741d4] text-xs truncate w-full"
                        >{{ course.title }}</span
                      >
                    </button>
                    <p
                      v-if="!m.courses || m.courses.length === 0"
                      class="text-xs text-slate-500 italic"
                    >
                      No courses found.
                    </p>
                  </div>

                  <!-- NEW: Unified Course Hub -->
                  <div
                    v-if="m.kind === 'unified_course_view'"
                    class="mt-3 flex flex-col gap-2 bg-indigo-50 border border-indigo-100 rounded-xl p-3 shadow-sm w-full"
                  >
                    <h3
                      class="font-bold text-sm text-indigo-900 border-b border-indigo-200 pb-2 mb-1 flex items-center justify-between gap-2"
                    >
                      <span class="truncate">{{ m.course.title }}</span>
                      <span
                        v-if="m.progress !== null"
                        class="shrink-0 text-[10px] font-bold text-white bg-indigo-500 px-1.5 py-0.5 rounded-md"
                        >{{ m.progress }}% done</span
                      >
                    </h3>
                    <div class="grid grid-cols-2 gap-2 mt-1">
                      <button
                        @click="
                          chatTargetFlow = 'assignments';
                          fetchAssignmentsOnly(m.course.id, '');
                        "
                        class="py-2 text-xs font-semibold bg-white text-indigo-700 border border-indigo-200 rounded-lg shadow-sm hover:bg-indigo-600 hover:text-white transition-all text-center"
                      >
                        Assignments
                      </button>
                      <button
                        @click="
                          chatTargetFlow = 'assessments';
                          fetchAssessmentsOnly(m.course.id, '');
                        "
                        class="py-2 text-xs font-semibold bg-white text-indigo-700 border border-indigo-200 rounded-lg shadow-sm hover:bg-indigo-600 hover:text-white transition-all text-center"
                      >
                        Assessments
                      </button>
                      <button
                        @click="
                          chatTargetFlow = 'quizzes';
                          fetchQuizzesOverview(m.course.id, '');
                        "
                        class="py-2 text-xs font-semibold bg-white text-indigo-700 border border-indigo-200 rounded-lg shadow-sm hover:bg-indigo-600 hover:text-white transition-all text-center"
                      >
                        Quizzes
                      </button>
                      <button
                        @click="
                          chatTargetFlow = 'grades';
                          fetchGrades(m.course.id, m.course.title);
                        "
                        class="py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition-all text-center"
                      >
                        Grades
                      </button>
                    </div>
                  </div>

                  <!-- NEW: Grades Detail -->
                  <div
                    v-if="m.kind === 'grades_detail'"
                    class="mt-3 flex flex-col gap-2 bg-white border border-slate-200 rounded-xl p-3 shadow-sm w-full"
                  >
                    <h3
                      class="font-bold text-sm text-slate-800 border-b border-slate-100 pb-2 flex justify-between items-center gap-2"
                    >
                      <span class="break-words line-clamp-2 max-w-[190px]">{{
                        m.course_name
                      }}</span>
                      <span
                        class="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full shrink-0"
                        >Grades</span
                      >
                    </h3>
                    <div
                      class="flex flex-col gap-2 mt-1"
                      v-if="m.grad_list && m.grad_list.length > 0"
                    >
                      <div
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50"
                      >
                        <span class="text-slate-600 font-medium"
                          >Assignment Marks</span
                        >
                        <span class="font-bold text-slate-900"
                          >{{
                            m.grad_list[0]?.assignment?.current_mark ?? "NA"
                          }}
                          /
                          {{
                            m.grad_list[0]?.percentages?.assignment ?? "?"
                          }}</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50"
                      >
                        <span class="text-slate-600 font-medium"
                          >Assessment Marks</span
                        >
                        <span class="font-bold text-slate-900"
                          >{{ m.grad_list[0]?.quize?.current_mark ?? "NA" }} /
                          {{
                            m.grad_list[0]?.percentages?.assessment ?? "?"
                          }}</span
                        >
                      </div>
                      <div
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50"
                      >
                        <span class="text-slate-600 font-medium"
                          >During Semester</span
                        >
                        <span class="font-bold text-emerald-700"
                          >{{ m.grad_list[0]?.overallmark ?? "NA" }} /
                          {{ m.grad_list[0]?.percentages?.total ?? "?" }}</span
                        >
                      </div>
                    </div>
                    <p
                      v-else
                      class="text-xs text-slate-500 text-center py-4 bg-slate-50 rounded-lg border border-slate-100 mt-2"
                    >
                      No grade data found for this course yet.
                    </p>
                  </div>

                  <!-- NEW: Semester-wide Grades Summary -->
                  <div
                    v-if="m.kind === 'semester_grades_detail'"
                    class="mt-3 flex flex-col gap-2 bg-white border border-slate-200 rounded-xl p-3 shadow-sm w-full"
                  >
                    <h3
                      class="font-bold text-sm text-slate-800 border-b border-slate-100 pb-2 flex justify-between items-center gap-2"
                    >
                      <span>Semester {{ m.semester?.semester }}</span>
                      <span
                        class="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full shrink-0"
                        >{{ m.summary?.gradedCourseCount }}/{{
                          m.summary?.courseCount
                        }}
                        graded</span
                      >
                    </h3>
                    <div
                      class="flex flex-col gap-2 mt-1"
                      v-if="m.courseRows && m.courseRows.length > 0"
                    >
                      <div
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50"
                      >
                        <span class="text-slate-600 font-medium"
                          >Avg. Assignment Marks</span
                        >
                        <span class="font-bold text-slate-900">{{
                          m.summary?.avgAssignment ?? "NA"
                        }}</span>
                      </div>
                      <div
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50"
                      >
                        <span class="text-slate-600 font-medium"
                          >Avg. Assessment Marks</span
                        >
                        <span class="font-bold text-slate-900">{{
                          m.summary?.avgAssessment ?? "NA"
                        }}</span>
                      </div>
                      <div
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50"
                      >
                        <span class="text-slate-600 font-medium"
                          >Avg. Overall</span
                        >
                        <span class="font-bold text-emerald-700">{{
                          m.summary?.avgOverall ?? "NA"
                        }}</span>
                      </div>
                      <h4
                        class="text-[11px] font-semibold text-slate-500 mt-2 mb-1"
                      >
                        PER-COURSE BREAKDOWN
                      </h4>
                      <div
                        v-for="(row, idx) in m.courseRows"
                        :key="'sg_' + idx"
                        class="flex justify-between items-center text-[11px] py-1 border-b border-slate-50 last:border-0"
                      >
                        <span class="text-slate-600 truncate max-w-[140px]">{{
                          row.courseTitle
                        }}</span>
                        <span class="font-semibold text-slate-800">{{
                          row.overallMark ?? "NA"
                        }}</span>
                      </div>
                    </div>
                    <p
                      v-else
                      class="text-xs text-slate-500 text-center py-4 bg-slate-50 rounded-lg border border-slate-100 mt-2"
                    >
                      No grade data found for this semester yet.
                    </p>
                  </div>

                  <!-- NEW: Assignment List -->
                  <div
                    v-if="m.kind === 'assignment_only_list'"
                    class="mt-3 flex flex-col gap-3"
                  >
                    <div v-if="m.assignments && m.assignments.length > 0">
                      <div class="flex flex-col gap-3">
                        <div
                          v-for="assign in m.assignments"
                          :key="'ast_' + assign.id"
                          @click="openAssignmentDetail(assign)"
                          class="bg-slate-50 border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-slate-100 transition-colors"
                        >
                          <div class="flex justify-between items-start gap-2">
                            <h4
                              class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                            >
                              {{ assign.title }}
                            </h4>
                            <span
                              v-if="
                                assign.student_assignment_data
                                  ?.verify_status === 'submitted'
                              "
                              class="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium"
                              >Pending</span
                            >
                            <span
                              v-else-if="
                                assign.student_assignment_data
                                  ?.verify_status === 'accepted'
                              "
                              class="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-medium"
                              >Accepted</span
                            >
                            <span
                              v-else-if="
                                assign.student_assignment_data
                                  ?.verify_status === 'rejected'
                              "
                              class="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 border border-red-200 font-medium"
                              >Rejected</span
                            >
                            <span
                              v-else
                              class="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 font-medium"
                              >Not Submitted</span
                            >
                          </div>
                          <p
                            class="text-[11px] text-slate-500 line-clamp-2 break-all"
                          >
                            {{ assign.description }}
                          </p>
                          <div
                            class="flex items-center justify-between mt-1 pt-2 border-t border-slate-200"
                          >
                            <span class="text-[10px] text-slate-500 font-medium"
                              >Due: {{ assign.duedate }}</span
                            >
                            <a
                              v-if="assign.file"
                              :href="assign.file"
                              target="_blank"
                              download
                              class="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                            >
                              <svg
                                class="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                ></path>
                              </svg>
                              File
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p v-else class="text-xs text-slate-500 italic">
                      No assignments found matching your criteria.
                    </p>
                  </div>

                  <!-- NEW: Assessment List -->
                  <div
                    v-if="m.kind === 'assessment_list'"
                    class="mt-3 flex flex-col gap-3"
                  >
                    <div v-if="m.assessments && m.assessments.length > 0">
                      <div class="flex flex-col gap-3">
                        <div
                          v-for="item in m.assessments"
                          :key="'qz_' + item.quiz.id"
                          @click="openAssessmentDetail(item)"
                          class="bg-blue-50 border border-blue-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
                        >
                          <div class="flex justify-between items-start gap-2">
                            <h4
                              class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                            >
                              {{ item.quiz.name }}
                            </h4>
                            <span
                              class="shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium"
                              :class="
                                item._status === 'completed'
                                  ? 'bg-green-100 text-green-700 border border-green-200'
                                  : item._status === 'no_attempts_left'
                                  ? 'bg-red-100 text-red-600 border border-red-200'
                                  : item._status === 'start'
                                  ? 'bg-amber-100 text-amber-700 border border-amber-200'
                                  : 'bg-slate-100 text-slate-500 border border-slate-200'
                              "
                              >{{ quizStatusLabel(item._status) }}</span
                            >
                          </div>
                          <p
                            class="text-[11px] text-slate-500 flex items-center gap-3"
                            v-if="item.quiz.question_attempt"
                          >
                            <span
                              >Attempts:
                              <strong class="text-slate-700"
                                >{{ item.quiz.student_attempt }}/{{
                                  item.quiz.question_attempt_count
                                }}</strong
                              ></span
                            >
                          </p>
                        </div>
                      </div>
                    </div>

                    <p v-else class="text-xs text-slate-500 italic">
                      No assessments found matching your criteria.
                    </p>
                  </div>

                  <!-- NEW: Quiz List (overview: course quizzes + lesson quizzes) -->
                  <div
                    v-if="m.kind === 'quiz_list'"
                    class="mt-3 flex flex-col gap-4 w-full"
                  >
                    <div v-if="m.assessments && m.assessments.length > 0">
                      <h4
                        class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Course Quizzes
                      </h4>
                      <div
                        v-for="item in m.assessments"
                        :key="'qz_' + item.quiz.id"
                        @click="openAssessmentDetail(item)"
                        class="bg-blue-50 border border-blue-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-blue-100 transition-colors mb-2 w-full"
                      >
                        <div class="flex justify-between items-start gap-2">
                          <h4
                            class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                          >
                            {{ item.quiz.name }}
                          </h4>
                          <span
                            class="shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium"
                            :class="
                              item._status === 'completed'
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : item._status === 'no_attempts_left'
                                ? 'bg-red-100 text-red-600 border border-red-200'
                                : item._status === 'start'
                                ? 'bg-amber-100 text-amber-700 border border-amber-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            "
                            >{{ quizStatusLabel(item._status) }}</span
                          >
                        </div>
                      </div>
                    </div>

                    <div v-if="m.lessonQuizzes && m.lessonQuizzes.length > 0">
                      <h4
                        class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Lesson Quizzes
                      </h4>
                      <div
                        v-for="item in m.lessonQuizzes"
                        :key="'lqz_' + item.quiz.id"
                        @click="openAssessmentDetail(item)"
                        class="bg-teal-50 border border-teal-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-teal-100 transition-colors mb-2 w-full"
                      >
                        <div class="flex justify-between items-start gap-2">
                          <h4
                            class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                          >
                            {{ item.quiz.name }}
                          </h4>
                          <div class="flex flex-col items-end gap-1">
                            <span
                              class="shrink-0 text-[10px] font-medium text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full"
                              >Lesson</span
                            >
                            <span
                              class="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                              :class="
                                item._status === 'completed'
                                  ? 'bg-green-100 text-green-700'
                                  : item._status === 'no_attempts_left'
                                  ? 'bg-red-100 text-red-600'
                                  : item._status === 'start'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-500'
                              "
                              >{{ quizStatusLabel(item._status) }}</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>

                    <p
                      v-if="
                        (!m.assessments || m.assessments.length === 0) &&
                        (!m.lessonQuizzes || m.lessonQuizzes.length === 0)
                      "
                      class="text-xs text-slate-500 italic"
                    >
                      No quizzes found.
                    </p>
                  </div>

                  <!-- NEW: Global Search Results -->
                  <div
                    v-if="m.kind === 'global_search_results'"
                    class="mt-3 flex flex-col gap-4"
                  >
                    <div v-if="m.courses && m.courses.length > 0">
                      <h4
                        class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Courses
                      </h4>
                      <div class="flex flex-col gap-2">
                        <button
                          v-for="course in m.courses"
                          :key="'c_' + course.id"
                          @click="openUnifiedCourseView(course)"
                          class="text-left w-full px-3 py-2 text-sm bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-100 transition-colors"
                        >
                          <span class="font-semibold block truncate">{{
                            course.title
                          }}</span>
                          <span class="text-[10px] text-indigo-500">{{
                            course.course_code
                          }}</span>
                        </button>
                      </div>
                    </div>

                    <div v-if="m.assignments && m.assignments.length > 0">
                      <h4
                        class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Assignments
                      </h4>
                      <div class="flex flex-col gap-2">
                        <div
                          v-for="assign in m.assignments"
                          :key="'ast_' + assign.id"
                          @click="openAssignmentDetail(assign)"
                          class="bg-slate-50 border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-slate-100 transition-colors"
                        >
                          <div class="flex justify-between items-start gap-2">
                            <h4
                              class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                            >
                              {{ assign.title }}
                            </h4>
                            <span
                              v-if="assign.courseInfo"
                              class="shrink-0 text-[10px] font-medium text-slate-500"
                              >{{ assign.courseInfo.course_code }}</span
                            >
                          </div>
                          <p
                            class="text-[11px] text-slate-500 mt-1 line-clamp-1"
                          >
                            {{ assign.description }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div v-if="m.assessments && m.assessments.length > 0">
                      <h4
                        class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Assessments / Quizzes
                      </h4>
                      <div class="flex flex-col gap-2">
                        <div
                          v-for="item in m.assessments"
                          :key="'gqz_' + item.quiz.id"
                          @click="openAssessmentDetail(item)"
                          class="bg-blue-50 border border-blue-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
                        >
                          <div class="flex justify-between items-start gap-2">
                            <h4
                              class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                            >
                              {{ item.quiz.name }}
                            </h4>
                            <span
                              v-if="item.courseInfo"
                              class="shrink-0 text-[10px] font-medium text-blue-500"
                              >{{ item.courseInfo.course_code }}</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="m.lessonQuizzes && m.lessonQuizzes.length > 0">
                      <h4
                        class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
                      >
                        Lesson Quizzes
                      </h4>
                      <div class="flex flex-col gap-2">
                        <div
                          v-for="item in m.lessonQuizzes"
                          :key="'lqz_' + item.quiz.id"
                          @click="openAssessmentDetail(item)"
                          class="bg-teal-50 border border-teal-200 rounded-xl p-3 shadow-sm flex flex-col gap-2 cursor-pointer hover:bg-teal-100 transition-colors"
                        >
                          <div class="flex justify-between items-start gap-2">
                            <h4
                              class="text-sm font-semibold text-slate-800 line-clamp-2 break-all"
                            >
                              {{ item.quiz.name }}
                            </h4>
                            <div class="flex flex-col items-end gap-1">
                              <span
                                v-if="item.courseInfo"
                                class="shrink-0 text-[10px] font-medium text-teal-600"
                                >{{ item.courseInfo.course_code }}</span
                              >
                              <span
                                class="text-[9px] bg-teal-100 text-teal-700 px-1 py-0.5 rounded"
                                >Lesson</span
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- NEW: Assignment Detail -->
                  <div
                    v-if="m.kind === 'assignment_detail'"
                    class="mt-3 flex flex-col gap-3 bg-white border border-slate-200 rounded-xl p-4 shadow-sm w-full"
                  >
                    <div
                      class="flex flex-col gap-1 border-b border-slate-100 pb-2"
                    >
                      <h3 class="text-base font-bold text-slate-800">
                        {{ m.assignment.title }}
                      </h3>
                      <div class="flex justify-between">
                        <span class="text-[11px] text-slate-500"
                          >Due: {{ m.assignment.duedate }}</span
                        >
                        <span
                          class="text-[11px] px-2 py-0.5 rounded-full font-medium"
                          :class="
                            m.assignment.student_assignment_data
                              ?.verify_status === 'submitted'
                              ? 'bg-amber-100 text-amber-700'
                              : m.assignment.student_assignment_data
                                  ?.verify_status === 'accepted'
                              ? 'bg-green-100 text-green-700'
                              : m.assignment.student_assignment_data
                                  ?.verify_status === 'rejected'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-slate-100 text-slate-600'
                          "
                        >
                          {{
                            m.assignment.student_assignment_data?.verify_status
                              ? m.assignment.student_assignment_data.verify_status
                                  .charAt(0)
                                  .toUpperCase() +
                                m.assignment.student_assignment_data.verify_status.slice(
                                  1,
                                )
                              : "Pending"
                          }}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 class="text-[11px] font-semibold text-slate-500 mb-1">
                        DESCRIPTION
                      </h4>
                      <p
                        class="text-xs text-slate-700 break-words whitespace-pre-wrap"
                      >
                        {{ m.assignment.description }}
                      </p>
                    </div>
                    <a
                      v-if="m.assignment.file"
                      :href="m.assignment.file"
                      target="_blank"
                      download
                      class="mt-1 flex items-center justify-center gap-2 w-full py-2 bg-indigo-50 text-indigo-600 font-semibold text-xs rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        ></path>
                      </svg>
                      Download Instruction File
                    </a>

                    <div
                      class="border-t border-slate-100 pt-3 mt-1"
                      v-if="m.assignment.student_assignment_data"
                    >
                      <h4 class="text-[11px] font-semibold text-slate-500 mb-2">
                        YOUR SUBMISSION
                      </h4>
                      <p
                        v-if="
                          m.assignment.student_assignment_data
                            .student_description
                        "
                        class="text-xs text-slate-700 break-words whitespace-pre-wrap mb-2 p-2 bg-slate-50 rounded border border-slate-100"
                      >
                        {{
                          m.assignment.student_assignment_data
                            .student_description
                        }}
                      </p>
                      <a
                        v-if="m.assignment.student_assignment_data.file"
                        :href="m.assignment.student_assignment_data.file"
                        target="_blank"
                        download
                        class="flex items-center gap-2 text-xs font-semibold text-green-600 hover:text-green-700"
                      >
                        <svg
                          class="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          ></path>
                        </svg>
                        View Submitted File
                      </a>
                      <div
                        class="flex justify-between items-center text-xs mt-3 pt-2 border-t border-slate-100"
                      >
                        <span class="text-slate-500 font-medium">Grade</span>
                        <span class="font-bold text-slate-800">{{
                          m.assignment.student_assignment_data.remark ||
                          "Not graded yet"
                        }}</span>
                      </div>
                      <div
                        v-if="
                          m.assignment.student_assignment_data.verify_status ===
                          'accepted'
                        "
                        class="mt-2"
                      >
                        <h4
                          class="text-[11px] font-semibold text-slate-500 mb-1"
                        >
                          FEEDBACK
                        </h4>
                        <p
                          class="text-xs text-slate-700 break-words whitespace-pre-wrap p-2 bg-slate-50 rounded border border-slate-100"
                        >
                          {{
                            m.assignment.student_assignment_data.feedback || "-"
                          }}
                        </p>
                      </div>
                    </div>
                    <div
                      v-else
                      class="border-t border-slate-100 pt-3 mt-1 text-center"
                    >
                      <button
                        @click="navigateToAssignment(m.assignment)"
                        class="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 w-full transition-colors shadow-sm"
                      >
                        Go to Submit Page
                      </button>
                    </div>
                  </div>

                  <!-- NEW: Assessment Detail -->
                  <div
                    v-if="m.kind === 'assessment_detail'"
                    class="mt-3 flex flex-col gap-3 bg-white border border-slate-200 rounded-xl p-4 shadow-sm w-full"
                  >
                    <div
                      class="flex flex-col gap-1 border-b border-slate-100 pb-2"
                    >
                      <h3 class="text-base font-bold text-slate-800">
                        {{ m.quiz.name }}
                      </h3>
                      <div class="flex justify-between items-center mt-1">
                        <span
                          class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                          :class="
                            m.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : m.status === 'no_attempts_left'
                              ? 'bg-red-100 text-red-600'
                              : m.status === 'start'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-500'
                          "
                          >{{ quizStatusLabel(m.status) }}</span
                        >
                        <span
                          v-if="m.quiz.question_attempt"
                          class="text-[11px] font-semibold text-slate-600"
                          >Attempts: {{ m.quiz.student_attempt }}/{{
                            m.quiz.question_attempt_count
                          }}</span
                        >
                        <span
                          v-else
                          class="text-[11px] font-semibold text-slate-600"
                          >No attempt limit</span
                        >
                      </div>
                    </div>

                    <div
                      class="flex flex-col gap-2"
                      v-if="m.attempts && m.attempts.length > 0"
                    >
                      <h4 class="text-[11px] font-semibold text-slate-500">
                        ATTEMPT HISTORY
                      </h4>
                      <div
                        v-for="(att, idx) in m.attempts"
                        :key="'att_' + idx"
                        class="flex justify-between items-center text-xs py-1.5 border-b border-slate-50 last:border-0"
                      >
                        <span class="text-slate-500">{{
                          formatAttemptDate(att.finished_at)
                        }}</span>
                        <span class="font-bold text-slate-800"
                          >{{ att.score ?? "-" }} /
                          {{
                            att.quiz?.max_value || att.quiz?.total_score || "-"
                          }}</span
                        >
                      </div>
                    </div>
                    <p v-else class="text-xs text-slate-500 italic">
                      No attempts yet.
                    </p>

                    <button
                      @click="navigateToAssessment(m.quiz)"
                      class="mt-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 w-full transition-colors shadow-sm"
                    >
                      {{
                        m.status === "completed" ||
                        m.status === "no_attempts_left"
                          ? "View Assessment"
                          : "Begin Assessment"
                      }}
                    </button>
                  </div>
                </div>
                <span class="text-[11px] text-slate-400 mt-1 block px-1">{{
                  m.time
                }}</span>
              </div>
            </div>

            <!-- User message -->
            <div v-else class="flex justify-end animate-[fadeUp_0.2s_ease-out]">
              <div
                class="max-w-[85%] sm:max-w-[80%] min-w-0 flex flex-col items-end"
              >
                <div
                  class="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-md"
                >
                  <p class="text-[13.5px] leading-relaxed break-words">
                    {{ m.text }}
                  </p>
                </div>
                <span
                  class="text-[11px] text-slate-400 mt-1 flex items-center gap-1 px-1"
                >
                  {{ m.time }}
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    class="w-3.5 h-3.5 text-indigo-400"
                  >
                    <path
                      d="M2 12l4 4L14 8"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8 12l4 4L22 6"
                      stroke="currentColor"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </template>

          <!-- Typing indicator -->
          <div
            v-if="isTyping"
            class="flex items-start gap-2.5 animate-[fadeUp_0.2s_ease-out]"
          >
            <div
              class="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center"
            >
              <img
                :key="'typing-face-' + moodPopKey"
                :src="robotMascotImg"
                alt=""
                draggable="false"
                class="w-6 h-6 object-contain object-top select-none [transform-origin:50%_55%] [will-change:transform] animate-[botIdleShake_2.4s_ease-in-out_infinite,botPop_0.4s_ease-out_1] motion-reduce:animate-none"
              />
            </div>
            <div
              class="bg-white rounded-2xl rounded-tl-sm px-4 py-3.5 shadow-md flex items-center gap-1.5"
            >
              <span
                class="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-[bounceDot_1.2s_infinite_ease-in-out]"
                style="animation-delay: 0ms"
              ></span>
              <span
                class="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-[bounceDot_1.2s_infinite_ease-in-out]"
                style="animation-delay: 150ms"
              ></span>
              <span
                class="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-[bounceDot_1.2s_infinite_ease-in-out]"
                style="animation-delay: 300ms"
              ></span>
            </div>
          </div>
        </div>

        <!-- No chip/bubble menu at all — plain free-text chat only. -->

        <!-- Input -->
        <div
          class="flex items-center gap-2 px-3 py-3 bg-white border-t border-slate-100 shrink-0"
          style="padding-bottom: max(0.75rem, env(safe-area-inset-bottom))"
        >
          <input
            v-model="input"
            @keyup.enter="sendMessage"
            type="text"
            inputmode="text"
            placeholder="Type your question..."
            class="flex-1 min-w-0 text-[13.5px] bg-slate-50 rounded-full px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-300 placeholder:text-slate-400"
          />
          <button
            @click="sendMessage"
            :disabled="!input.trim()"
            class="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-95 transition-all"
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" fill="none" class="w-4 h-4">
              <path
                d="M4 12l16-7-6 16-2.5-6.5L4 12z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import robotMascotImg from "../../assets/robot-mascot.gif";

export default {
  name: "ChatBot",

  props: {
    userName: {
      type: String,
      default: "Student",
    },
  },

  emits: ["send"],

  data() {
    return {
      robotMascotImg,
      isOpen: false,
      isFullscreen: false,
      input: "",
      isTyping: false,
      hasUnread: true,
      chatMode: "chat",
      chatSelectedSemesterId: null,
      chatSelectedCourseId: null,
      chatTargetFlow: "unified",
      proactiveNotice: null,
      moodPopKey: 0,
      moodTimer: null,
      moodIntervalMs: 3000,
      demoMetrics: {
        daysSinceLastLogin: 4,
        quizScore: 42,
        quizAttempts: 2,
        videoDropRate: 65,
        completionPercent: 40,
        highEngagement: false,
      },

      messages: [
        {
          id: 1,
          from: "bot",
          text: "",
          time: "",
        },
      ],

      // ---- Straight from the "Messages" sheet of LMS_AlliO.xlsx ----
      categories: {
        "Inactivity / Drop-off": {
          assumption: "Less than 35% progress",
          action: "Scheduled Immediate activity",
          messages: [
            "It’s been a few days. That’s okay—progress isn’t always linear. Ready to restart with something quick?",
            "Life gets busy. Let’s pick up from where you left off—no pressure, just momentum.",
            "Even a 10-minute session today can restart your flow. Want something light?",
            "You started strong—don’t let a small pause turn into a stop.",
            "Consistency beats intensity. Let’s get back gently.",
            "You’re closer than you think. Just one step today.",
            "Paused journeys still count. Let’s continue.",
            "We kept your progress safe—ready when you are.",
            "No guilt, just growth. Let’s begin again.",
            "A small restart today is better than a perfect tomorrow.",
            "Momentum is waiting for you—just one click away.",
            "Your learning path is still open—come back in.",
            "You haven’t lost anything. Let’s move forward.",
            "Start small, stay consistent—that’s how this works.",
            "Welcome back—shall we ease into it?",
          ],
        },
        Overwhelm: {
          assumption: "Staying long within module",
          action: "Book a slot with Faculty / Ticketing system",
          messages: [
            "That looked like a heavy module. Want me to simplify it?",
            "Too much at once? Let’s break it into smaller pieces.",
            "It’s okay to feel stuck—this topic challenges many learners.",
            "Let’s slow it down and take it step by step.",
            "Complex doesn’t mean impossible. Let’s simplify.",
            "You don’t have to finish everything today.",
            "Try just one concept—that’s enough for now.",
            "Clarity comes in layers. Let’s start with the basics.",
            "Feeling overloaded? I’ve got a simpler version for you.",
            "Take it one idea at a time—you’ll get there.",
          ],
        },
        "Confusion / Rewatching": {
          assumption: "Revisits / Abortions / No forum activity",
          action: "Suggest a peer mentor (open personal chat of peer mentor)",
          messages: [
            "You revisited this—great sign. Want a simpler explanation?",
            "Let’s try a real-world example to make this clearer.",
            "Confusion is part of learning. Shall we break it down?",
            "Would a visual explanation help here?",
            "Let’s look at this from a different angle.",
            "You’re asking the right questions—keep going.",
            "Let’s simplify this concept together.",
            "This topic clicks with one small insight—want it?",
            "Almost there—just one more layer of clarity.",
            "You’re closer to understanding than you think.",
          ],
        },
        "Frustration (Low Scores / Failures)": {
          assumption: "Repeated low marks in various attempts",
          action: "Re attempt page",
          messages: [
            "Tough one—most learners struggle here. You’re not alone.",
            "This isn’t failure—it’s feedback. Let’s try again.",
            "You’re learning, not losing. Let’s improve step by step.",
            "Retrying is where real growth happens.",
            "Let’s fix just one mistake at a time.",
            "You’ve got this—just needs one more attempt.",
            "Progress hides inside mistakes—let’s find it.",
            "Don’t judge yourself—adjust your approach.",
            "Try again with a fresh perspective.",
            "Every expert started exactly here.",
            "You’re improving—even if it doesn’t feel like it.",
            "Let’s convert this into a win.",
          ],
        },
        "High Motivation / Momentum": {
          assumption:
            "Successful completion of assessments / Assignments / 80% progress",
          action:
            "Willingness to be peer mentor (pop up asking for acceptance)",
          messages: [
            "You’re on a roll—keep this momentum going!",
            "Great pace—this is how top learners move.",
            "You’re building something powerful here.",
            "This consistency will pay off big.",
            "Want to try a challenge next?",
            "You’re ahead of the curve—keep pushing.",
            "Momentum like this is rare—protect it.",
            "You’re doing more than required—that’s leadership.",
            "Let’s level up while you’re in flow.",
            "This is peak learning mode—stay in it.",
          ],
        },
        "Mid-Video Drop": {
          assumption: "Live class dropout",
          action: "Point to the same module lessons",
          messages: [
            "Did something not click? Want a quick summary?",
            "Let me simplify that part for you.",
            "That section can be tricky—shall I explain it differently?",
            "Try a shorter version of this concept?",
            "Want the key takeaway in 2 minutes?",
            "Let’s make this easier to understand.",
            "Pause, reset, and try again—you’ll get it.",
            "Need a shortcut explanation?",
          ],
        },
        "Deep Engagement (Rewatch + Notes)": {
          assumption: "100% participation with 85% of desired time stamps",
          action: "Point to the reference folder / Additional materials",
          messages: [
            "You’re going deep—that’s how mastery happens.",
            "Revisiting concepts is a smart strategy.",
            "This level of focus sets you apart.",
            "You’re not just learning—you’re understanding.",
            "This is how experts are made.",
            "Your effort is clearly visible—keep going.",
            "You’re building strong foundations.",
            "Excellent learning behavior—stay consistent.",
          ],
        },
        "Course Completion Push": {
          assumption:
            "15 days before SEE \nNot completed the participation and/or Assessments and/or Assignements",
          action:
            "Point to the incompletions (prioritise the highest progressed course)",
          messages: [
            "You’re close—don’t stop now.",
            "Just a few steps left—finish strong.",
            "Completion is within reach—go for it.",
            "You’ve come this far—cross the line.",
            "Final stretch—give it your best.",
            "Almost done—this is where it counts.",
            "You’re closer than most learners get.",
            "Complete this—you’ll thank yourself later.",
            "One last push—you’ve got it.",
            "Finish what you started—it matters.",
          ],
        },
        "Achievement / Completion": {
          assumption: "On completion of 75%+, Assessment ans Assignments",
          action: "Congratulation message",
          messages: [
            "Well done—you made it!",
            "That’s a strong achievement—be proud.",
            "You didn’t quit—that’s what counts.",
            "Completion is a big deal—celebrate it.",
            "You’ve earned this milestone.",
            "Great work—what’s next?",
            "This is just the beginning.",
          ],
        },
        "Identity Building": {
          assumption:
            "Every week login, Live class attendance, Activities in regula intervals",
          action:
            "Pop up - proposing to be an ambassidor for AUO (Social media featuring)",
          messages: [
            "You’re becoming a consistent learner.",
            "This discipline will define your success.",
            "You’re building a professional mindset.",
            "This is how leaders learn.",
            "You’re investing in your future self.",
            "Growth looks exactly like this.",
            "You’re showing commitment—that’s rare.",
            "This habit will take you far.",
            "You’re evolving with every session.",
            "You’re not just learning—you’re transforming.",
          ],
        },
      },

      // ---- Keyword triggers modelling the "logic" sheet rules ----
      // e.g. IF no_login > 3 days -> Category 1 (Inactivity / Drop-off)
      triggers: {
        Assignments: ["assignment", "homework", "task"],
        Assessments: ["assessment", "assessments", "exam"],
        Quizzes: ["quiz", "test", "lesson quiz", "quiz list"],
        Grades: ["grade", "score", "marks", "gradebook", "result"],
        Courses: ["course", "class", "subject"],
        Semesters: ["semester", "term"],
        "Inactivity / Drop-off": [
          "login",
          "logged in",
          "log in",
          "havent been",
          "been a while",
          "few days",
          "3 days",
          "three days",
          "came back",
          "away for",
          "not been active",
          "inactive",
        ],
        Overwhelm: [
          "too much",
          "overwhelm",
          "heavy module",
          "stuck on",
          "long time on",
          "cant finish",
          "cannot finish",
          "too long",
          "too complex",
        ],
        "Confusion / Rewatching": [
          "confus",
          "rewatch",
          "watched again",
          "doesnt make sense",
          "don't understand",
          "dont understand",
          "not clear",
          "unclear",
        ],
        "Frustration (Low Scores / Failures)": [
          "failed",
          "fail",
          "low score",
          "low marks",
          "cant pass",
          "cannot pass",
          "quiz again",
          "retake",
          "bad grade",
          "score low",
        ],
        "High Motivation / Momentum": [
          "doing great",
          "on a roll",
          "completed most",
          "ahead of schedule",
          "feeling motivated",
          "going well",
        ],
        "Mid-Video Drop": [
          "dropped off",
          "left the live class",
          "disconnected",
          "live class",
          "mid video",
          "exited the class",
        ],
        "Deep Engagement (Rewatch + Notes)": [
          "took notes",
          "rewatched",
          "reference material",
          "extra material",
          "deep dive",
        ],
        "Course Completion Push": [
          "deadline",
          "exam soon",
          "see exam",
          "not completed",
          "pending assignment",
          "running out of time",
        ],
        "Achievement / Completion": [
          "i completed",
          "i finished",
          "i passed",
          "done with the course",
          "completed the course",
        ],
        "Identity Building": [
          "every week",
          "consistent",
          "streak",
          "regular login",
          "ambassador",
        ],
      },
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),
  },

  created() {
    this.messages[0].text = `Hello ${this.userName}! \u{1F44B}\nHow can I help you today?`;
    this.messages[0].time = this.currentTime();
  },

  mounted() {
    this.updateBreakpoint();
    window.addEventListener("resize", this.updateBreakpoint);
    setTimeout(() => {
      this.evaluateLogicRules();
    }, 1200);

    // Retriggers the mascot's little "pop" animation every few seconds.
    this.moodTimer = setInterval(() => {
      this.moodPopKey += 1;
    }, this.moodIntervalMs);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.updateBreakpoint);
    if (this.moodTimer) {
      clearInterval(this.moodTimer);
      this.moodTimer = null;
    }
  },

  methods: {
    currentTime() {
      return new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatAttemptDate(dateString) {
      if (!dateString) return "-";
      return new Date(dateString).toLocaleDateString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    updateBreakpoint() {
      this.isFullscreen = window.innerWidth < 640;
    },
    evaluateLogicRules() {
      const m = this.demoMetrics;
      let categoryKey = null;

      if (m.daysSinceLastLogin > 3) {
        categoryKey = "Inactivity / Drop-off";
      } else if (m.quizScore < 50 && m.quizAttempts >= 2) {
        categoryKey = "Frustration (Low Scores / Failures)";
      } else if (m.videoDropRate > 60) {
        categoryKey = "Mid-Video Drop";
      } else if (m.completionPercent > 70) {
        categoryKey = "Course Completion Push";
      } else if (m.highEngagement) {
        categoryKey =
          Math.random() < 0.5
            ? "High Motivation / Momentum"
            : "Deep Engagement (Rewatch + Notes)";
      }

      if (!categoryKey) return;

      const cat = this.categories[categoryKey];
      const message =
        cat.messages[Math.floor(Math.random() * cat.messages.length)];

      this.proactiveNotice = {
        categoryKey,
        message,
        preview: message.length > 60 ? message.slice(0, 57) + "..." : message,
      };
      this.hasUnread = true;
    },

    // Tapping the pill opens the chat with that message already delivered.
    openFromNotice() {
      const notice = this.proactiveNotice;
      this.proactiveNotice = null;
      this.toggleOpen();

      if (notice) {
        this.pushBotReply(notice.message, "message", "course");
        const cat = this.categories[notice.categoryKey];
        this.isTyping = true;
        setTimeout(() => {
          this.isTyping = false;
          this.pushBotReply(cat.action, "action");
        }, 500);
      }
    },

    dismissNotice() {
      this.proactiveNotice = null;
    },

    resetChat() {
      this.messages = [
        {
          id: Date.now(),
          from: "bot",
          text: `Hello ${this.userName}! \u{1F44B}\nHow can I help you today?`,
          time: this.currentTime(),
        },
      ];

      this.input = "";
      this.isTyping = false;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesEl;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    toggleOpen() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.hasUnread = false;
        this.scrollToBottom();
      }
    },

    closeChat() {
      this.isOpen = false;
    },

    pushUserMessage(text) {
      this.messages.push({
        id: Date.now() + Math.random(),
        from: "user",
        text,
        time: this.currentTime(),
      });
      this.scrollToBottom();
    },

    pushBotReply(text, kind = "message", tag = null, extra = {}) {
      this.messages.push({
        id: Date.now() + Math.random(),
        from: "bot",
        text,
        kind,
        tag,
        time: this.currentTime(),
        ...extra,
      });
      this.scrollToBottom();
    },

    sendMessage() {
      const text = this.input.trim();
      if (!text) return;

      this.pushUserMessage(text);
      this.input = "";

      if (
        this.chatMode === "assignments_search" ||
        this.chatMode === "assessments_search"
      ) {
        const triggeredCat = this.matchTrigger(text);
        if (
          text.toLowerCase() === "cancel" ||
          text.toLowerCase() === "exit" ||
          text.toLowerCase() === "back" ||
          this.isGreeting(text) ||
          this.isCourseInfoQuestion(text) ||
          triggeredCat
        ) {
          this.chatMode = "chat";
          if (
            text.toLowerCase() === "cancel" ||
            text.toLowerCase() === "exit" ||
            text.toLowerCase() === "back"
          ) {
            this.pushBotReply("Exited search. Returning to chat!", "message");
            return;
          }
        } else {
          if (this.chatMode === "assignments_search") {
            this.fetchAssignmentsOnly(this.chatSelectedCourseId, text);
          } else {
            this.fetchAssessmentsOnly(this.chatSelectedCourseId, text);
          }
          return;
        }
      }

      this.respondToFreeText(text);
    },

    respondToFreeText(text) {
      this.isTyping = true;

      setTimeout(() => {
        this.isTyping = false;

        const triggeredCategory = this.matchTrigger(text);
        if (triggeredCategory === "Assignments") {
          this.startSemesterFlow("assignments");
          return;
        } else if (triggeredCategory === "Assessments") {
          this.startSemesterFlow("assessments");
          return;
        } else if (triggeredCategory === "Grades") {
          this.startSemesterFlow("grades");
          return;
        } else if (triggeredCategory === "Quizzes") {
          this.startSemesterFlow("quizzes");
          return;
        } else if (
          triggeredCategory === "Semesters" ||
          triggeredCategory === "Courses"
        ) {
          this.startSemesterFlow("unified");
          return;
        } else if (triggeredCategory) {
          const cat = this.categories[triggeredCategory];
          const reply =
            cat.messages[Math.floor(Math.random() * cat.messages.length)];

          this.pushBotReply(reply, "message", "course");

          // Send the associated action as a short follow-up bubble.
          this.isTyping = true;
          setTimeout(() => {
            this.isTyping = false;
            this.pushBotReply(cat.action, "action");
          }, 500);
          return;
        }

        if (this.isCourseInfoQuestion(text)) {
          this.startSemesterFlow("unified");
          return;
        }

        if (this.isGreeting(text)) {
          this.pushBotReply(
            "Hey! 👋 How can I help you today? Ask me about your courses, or tell me how your progress is going.",
            "message",
          );
          return;
        }
        this.globalSearch(text);
      }, 450);
    },

    isGreeting(text) {
      const t = text.toLowerCase().trim();
      return /^(h+i+|h+e+y+|h+e+l+l+o+|good\s?(morning|afternoon|evening))[\s!.,?]*$/.test(
        t,
      );
    },

    isCourseInfoQuestion(text) {
      const t = text.toLowerCase();
      return (
        t.includes("course") ||
        t.includes("subject") ||
        t.includes("class") ||
        t.includes("syllabus") ||
        t.includes("curriculum")
      );
    },

    matchTrigger(text) {
      const t = text.toLowerCase();
      let bestKey = null;
      let bestScore = 0;

      for (const [key, words] of Object.entries(this.triggers)) {
        let score = 0;
        for (const w of words) {
          if (t.includes(w)) score += 1;
        }
        if (score > bestScore) {
          bestScore = score;
          bestKey = key;
        }
      }

      return bestScore > 0 ? bestKey : null;
    },

    async startSemesterFlow(target = "unified") {
      this.chatMode = "semester_selection";
      this.chatTargetFlow = target;
      this.isTyping = true;
      try {
        let url = `${process.env.VUE_APP_API}/student-semester-get/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}`;
        const response = await axios.get(url);
        let semesters = [];
        if (response.data?.data?.status === 200) {
          semesters = response.data.data.data[0]?.semester || [];
        }

        semesters = semesters.map((sem) => {
          let count = 0;
          if (sem.course && Array.isArray(sem.course)) {
            count = sem.course.filter(
              (c) => c.published_course?.published,
            ).length;
          }
          return { ...sem, courseCount: count };
        });

        semesters = [...semesters].reverse();

        this.pushBotReply(
          "Please select a semester:",
          "semester_selection",
          null,
          { semesters },
        );
      } catch (error) {
        console.error("Error fetching semesters for chatbot:", error);
        this.pushBotReply(
          "Sorry, I couldn't fetch your semesters right now.",
          "message",
        );
        this.chatMode = "chat";
      } finally {
        this.isTyping = false;
      }
    },

    async selectSemesterForCourses(sem) {
      if (this.chatMode !== "semester_selection") return;
      this.pushUserMessage(`Semester ${sem.semester}`);
      this.chatSelectedSemesterId = sem.id;
      this.chatMode = "course_selection";
      this.isTyping = true;

      try {
        let url = `${process.env.VUE_APP_API}/student-semester-get/${this.userInfo.user.id}/?semester__id=${sem.id}&Key=${this.userInfo.token_key}`;
        const response = await axios.get(url);
        let courses = [];
        if (response.data?.data?.status === 200) {
          const semData = response.data.data.data[0]?.semester || [];
          semData.forEach((s) => {
            if (s.course && Array.isArray(s.course)) {
              s.course.forEach((c) => {
                if (c.published_course?.published) {
                  courses.push(c);
                }
              });
            }
          });
        }

        const uniqueCourses = [];
        const seen = new Set();
        for (let c of courses) {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            uniqueCourses.push(c);
          }
        }

        const totalCount = uniqueCourses.length;
        let msgText = `Semester ${sem.semester} has ${totalCount} course${
          totalCount === 1 ? "" : "s"
        }. Please select one to view details:`;
        if (this.chatTargetFlow === "assignments")
          msgText = `Semester ${sem.semester} has ${totalCount} course${
            totalCount === 1 ? "" : "s"
          }. Please select one to view its assignments:`;
        if (this.chatTargetFlow === "assessments")
          msgText = `Semester ${sem.semester} has ${totalCount} course${
            totalCount === 1 ? "" : "s"
          }. Please select one to view its assessments:`;
        if (this.chatTargetFlow === "grades")
          msgText = `Semester ${sem.semester} has ${totalCount} course${
            totalCount === 1 ? "" : "s"
          }. Please select one to view its course-wise grades:`;
        if (this.chatTargetFlow === "quizzes")
          msgText = `Semester ${sem.semester} has ${totalCount} course${
            totalCount === 1 ? "" : "s"
          }. Please select one to view its quizzes:`;

        if (this.chatTargetFlow === "grades") {
          await this.fetchSemesterGrades(sem, uniqueCourses);
        }

        this.pushBotReply(msgText, "course_selection", null, {
          courses: uniqueCourses,
        });
      } catch (error) {
        console.error("Error fetching courses for chatbot:", error);
        this.pushBotReply(
          "Sorry, I couldn't fetch your courses right now.",
          "message",
        );
        this.chatMode = "chat";
      } finally {
        this.isTyping = false;
      }
    },

    async fetchSemesterGrades(sem, courses) {
      if (!courses || !courses.length) return;
      try {
        const results = await Promise.all(
          courses.map((c) =>
            axios
              .get(
                `${process.env.VUE_APP_API}/user-skill/?Key=${this.userInfo.token_key}&course__id=${c.id}&user__id=${this.userInfo.user.id}`,
              )
              .then((res) => ({ course: c, grad: res.data?.data?.[0] || null }))
              .catch(() => ({ course: c, grad: null })),
          ),
        );

        const withData = results.filter((r) => r.grad);
        const courseRows = withData.map((r) => ({
          courseTitle: r.course.title,
          assignmentMark: r.grad.assignment?.current_mark ?? null,
          assessmentMark: r.grad.quize?.current_mark ?? null,
          overallMark: r.grad.overallmark ?? null,
        }));

        const avg = (values) => {
          const nums = values
            .filter((v) => v !== null && v !== undefined && !isNaN(v))
            .map(Number);
          if (!nums.length) return null;
          return (
            Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 100) /
            100
          );
        };

        const summary = {
          avgAssignment: avg(courseRows.map((c) => c.assignmentMark)),
          avgAssessment: avg(courseRows.map((c) => c.assessmentMark)),
          avgOverall: avg(courseRows.map((c) => c.overallMark)),
          courseCount: courses.length,
          gradedCourseCount: withData.length,
        };

        this.pushBotReply(
          `Here is your semester-wide grade summary for Semester ${sem.semester}:`,
          "semester_grades_detail",
          null,
          { semester: sem, courseRows, summary },
        );
      } catch (error) {
        console.error("Error fetching semester grades for chatbot:", error);
      }
    },

    async selectCourseForAssignments(course) {
      if (this.chatMode !== "course_selection" && this.chatMode !== "chat")
        return;
      this.pushUserMessage(course.title);
      this.chatSelectedCourseId = course.id;
      this.chatMode = "chat";

      if (this.chatTargetFlow === "assignments") {
        await this.fetchAssignmentsOnly(course.id, "");
      } else if (this.chatTargetFlow === "assessments") {
        await this.fetchAssessmentsOnly(course.id, "");
      } else if (this.chatTargetFlow === "grades") {
        await this.fetchGrades(course.id, course.title);
      } else if (this.chatTargetFlow === "quizzes") {
        await this.fetchQuizzesOverview(course.id, "");
      } else {
        // Unified course view
        this.openUnifiedCourseView(course);
      }
    },

    getQuizStatus(quiz) {
      if (!quiz) return "not_submitted";
      if (!quiz.question_attempt) {
        if (
          quiz.qualify_certificate === true &&
          quiz.quiztakers_set?.completed === true
        ) {
          return "completed";
        }
        return "not_submitted";
      }
      if (quiz.student_attempt === 0) return "not_submitted";
      if (quiz.student_attempt === quiz.question_attempt_count)
        return "no_attempts_left";
      return quiz.quiztakers_set?.completed === true ? "completed" : "start";
    },

    quizStatusLabel(status) {
      return (
        {
          not_submitted: "Not Submitted",
          start: "In Progress",
          completed: "Completed",
          no_attempts_left: "No Attempts Left",
        }[status] || "Not Submitted"
      );
    },

    async openUnifiedCourseView(course) {
      this.chatSelectedCourseId = course.id;
      this.pushBotReply(
        `Here is the hub for ${course.title}:`,
        "unified_course_view",
        null,
        { course, progress: null },
      );
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/student-buy-course-percentage/?Key=${this.userInfo.token_key}`,
        );
        const list = res.data?.data?.[0]?.course || [];
        const match = list.find((c) => c.id === course.id);
        if (match) {
          const lastMsg = this.messages[this.messages.length - 1];
          if (
            lastMsg &&
            lastMsg.kind === "unified_course_view" &&
            lastMsg.course.id === course.id
          ) {
            lastMsg.progress = match.completion_percentage ?? null;
          }
        }
      } catch (error) {
        console.error("Error fetching course progress for chatbot:", error);
      }
    },

    async fetchAssignmentsOnly(courseId, searchQuery = "") {
      this.isTyping = true;
      try {
        let assignments = [];

        const assignResponse = await axios
          .get(
            `${process.env.VUE_APP_API}/Inst_Assignment/view/?course_id=${courseId}&Key=${this.userInfo.token_key}&page=1&publish=true&search=${searchQuery}`,
          )
          .catch((e) => {
            console.log("Assign err", e);
            return null;
          });

        if (assignResponse && assignResponse.data?.data?.status === 200) {
          assignments = assignResponse.data.data.data || [];
        }

        let replyText = searchQuery
          ? `Assignments matching "${searchQuery}":\n(Type "cancel" to exit search)`
          : `Here are your assignments:\n(Type to search, or "cancel" to exit)`;

        this.chatMode = "assignments_search";
        this.pushBotReply(replyText, "assignment_only_list", null, {
          assignments,
        });
      } catch (error) {
        console.error("Error fetching assignments for chatbot:", error);
        this.pushBotReply(
          "Sorry, I couldn't fetch assignments right now.",
          "message",
        );
        this.chatMode = "chat";
      } finally {
        this.isTyping = false;
      }
    },

    async fetchAssessmentsOnly(courseId, searchQuery = "") {
      this.isTyping = true;
      try {
        let assessments = [];

        const assessResponse = await axios
          .get(
            `${process.env.VUE_APP_API}/course_quizzes_detail/${courseId}/?Key=${this.userInfo.token_key}&qualify_certificate=true&search=${searchQuery}`,
          )
          .catch((e) => {
            console.log("Assess err", e);
            return null;
          });

        if (assessResponse && assessResponse.data?.data) {
          assessments = assessResponse.data.data || [];
        }

        if (searchQuery) {
          const sq = searchQuery.toLowerCase();
          assessments = assessments.filter(
            (q) => q.quiz?.name && q.quiz.name.toLowerCase().includes(sq),
          );
        }

        assessments = assessments.map((a) => ({
          ...a,
          _status: this.getQuizStatus(a.quiz),
        }));

        let replyText = searchQuery
          ? `Assessments matching "${searchQuery}":\n(Type "cancel" to exit search)`
          : `Here are your assessments:\n(Type to search, or "cancel" to exit)`;

        this.chatMode = "assessments_search";
        this.pushBotReply(replyText, "assessment_list", null, { assessments });
      } catch (error) {
        console.error("Error fetching assessments for chatbot:", error);
        this.pushBotReply(
          "Sorry, I couldn't fetch assessments right now.",
          "message",
        );
        this.chatMode = "chat";
      } finally {
        this.isTyping = false;
      }
    },

    async fetchGrades(courseId, courseName) {
      this.isTyping = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/user-skill/?Key=${this.userInfo.token_key}&course__id=${courseId}&user__id=${this.userInfo.user.id}`,
        );
        let grad_list = [];
        if (response.data && response.data.data) {
          grad_list = response.data.data;
        }
        this.pushBotReply(
          `Here are your grades for ${courseName}:`,
          "grades_detail",
          null,
          { grad_list, course_name: courseName },
        );
      } catch (error) {
        console.error("Error fetching grades for chatbot:", error);
        this.pushBotReply(
          "Sorry, an error occurred while fetching your grades.",
          "message",
          "general",
        );
      } finally {
        this.isTyping = false;
      }
    },

    async fetchQuizzesOverview(courseId, searchQuery = "") {
      this.isTyping = true;
      try {
        const courseQuizRes = await axios
          .get(
            `${process.env.VUE_APP_API}/course_quizzes_detail/${courseId}/?Key=${this.userInfo.token_key}&qualify_certificate=true&search=${searchQuery}`,
          )
          .catch(() => null);
        let assessments = [];
        if (courseQuizRes && courseQuizRes.data?.data) {
          assessments = courseQuizRes.data.data;
        }

        // Lesson quizzes
        const lessonQuizRes = await axios
          .get(
            `${process.env.VUE_APP_API}/lesson_quizzes_detail/${courseId}/?Key=${this.userInfo.token_key}&qualify_certificate=True`,
          )
          .catch(() => null);
        let lessonQuizzes = [];
        if (lessonQuizRes && lessonQuizRes.data?.data) {
          lessonQuizzes = lessonQuizRes.data.data;
        }

        if (searchQuery) {
          const sq = searchQuery.toLowerCase();
          lessonQuizzes = lessonQuizzes.filter(
            (q) => q.quiz?.name && q.quiz.name.toLowerCase().includes(sq),
          );
        }

        assessments = assessments.map((a) => ({
          ...a,
          _status: this.getQuizStatus(a.quiz),
        }));
        lessonQuizzes = lessonQuizzes.map((a) => ({
          ...a,
          _status: this.getQuizStatus(a.quiz),
        }));

        const completedCourseQuiz = assessments.filter(
          (a) => a._status === "completed",
        ).length;
        const completedLessonQuiz = lessonQuizzes.filter(
          (a) => a._status === "completed",
        ).length;

        const countsText = `Quizzes: ${assessments.length} available (${completedCourseQuiz} completed) · Lesson quizzes: ${lessonQuizzes.length} available (${completedLessonQuiz} completed)`;

        this.pushBotReply(countsText, "quiz_list", null, {
          assessments,
          lessonQuizzes,
        });
      } catch (error) {
        console.error("Error fetching quizzes for chatbot:", error);
        this.pushBotReply(
          "Sorry, an error occurred while fetching your quizzes.",
          "message",
          "general",
        );
      } finally {
        this.isTyping = false;
      }
    },

    openAssignmentDetail(assignment) {
      this.pushBotReply(
        "Here are the details for that assignment:",
        "assignment_detail",
        null,
        { assignment },
      );
    },

    async openAssessmentDetail(item) {
      const quiz = item.quiz;
      const status = item._status || this.getQuizStatus(quiz);
      let attempts = [];
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/quiztaker__quizzes_pk/${quiz.id}/?Key=${this.userInfo.token_key}&user__id=${this.userInfo.user.id}`,
        );
        if (res.data?.status === 200) {
          attempts = res.data.data || [];
        }
      } catch (error) {
        console.error("Error fetching assessment attempts for chatbot:", error);
      }
      this.pushBotReply(
        "Here are the details for that assessment:",
        "assessment_detail",
        null,
        { quiz, status, attempts },
      );
    },

    navigateToAssignment(assignment) {
      if (assignment.course) {
        this.$router.push({
          name: "StudentCourseProgress",
          params: { course_id: assignment.course },
        });
        this.closeChat();
      } else {
        this.$router.push({ name: "StudentMyCourse" });
        this.closeChat();
      }
    },

    navigateToAssessment(quiz) {
      // Similar to assignmnet, navigate to the specific course progress
      if (quiz.course) {
        this.$router.push({
          name: "StudentCourseProgress",
          params: { course_id: quiz.course },
        });
        this.closeChat();
      } else {
        this.$router.push({ name: "StudentMyCourse" });
        this.closeChat();
      }
    },

    async globalSearch(searchText) {
      this.isTyping = true;
      try {
        const currentSem = this.userInfo?.current_semester?.id || null;
        let url = `${process.env.VUE_APP_API}/student-semester-get/${this.userInfo.user.id}/?Key=${this.userInfo.token_key}`;
        if (currentSem) url += `&semester__id=${currentSem}`;

        const response = await axios.get(url);
        let courses = [];
        if (response.data?.data?.status === 200) {
          const semData = response.data.data.data[0]?.semester || [];
          semData.forEach((sem) => {
            if (sem.course && Array.isArray(sem.course)) {
              sem.course.forEach((c) => {
                if (c.published_course?.published) courses.push(c);
              });
            }
          });
        }

        const uniqueCourses = [];
        const seen = new Set();
        for (let c of courses) {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            uniqueCourses.push(c);
          }
        }

        // Match in courses
        const sq = searchText.toLowerCase();
        const matchedCourses = uniqueCourses.filter(
          (c) =>
            c.title.toLowerCase().includes(sq) ||
            (c.course_code && c.course_code.toLowerCase().includes(sq)),
        );

        // Match in assignments/assessments across all courses concurrently
        let allAssignments = [];
        let allAssessments = [];
        let allLessonQuizzes = [];

        await Promise.all(
          uniqueCourses.map(async (c) => {
            // Assignments
            const assignRes = await axios
              .get(
                `${process.env.VUE_APP_API}/Inst_Assignment/view/?course_id=${c.id}&Key=${this.userInfo.token_key}&page=1&publish=true&search=${searchText}`,
              )
              .catch(() => null);
            if (assignRes && assignRes.data?.data?.status === 200) {
              const data = assignRes.data.data.data || [];
              data.forEach((a) => allAssignments.push({ ...a, courseInfo: c }));
            }
            // Assessments
            const assessRes = await axios
              .get(
                `${process.env.VUE_APP_API}/course_quizzes_detail/${c.id}/?Key=${this.userInfo.token_key}&qualify_certificate=true&search=${searchText}`,
              )
              .catch(() => null);
            if (assessRes && assessRes.data?.data) {
              const data = assessRes.data.data || [];
              const filtered = data.filter(
                (q) => q.quiz?.name && q.quiz.name.toLowerCase().includes(sq),
              );
              filtered.forEach((q) =>
                allAssessments.push({
                  ...q,
                  courseInfo: c,
                  _status: this.getQuizStatus(q.quiz),
                }),
              );
            }
            // Lesson Quizzes
            const lessonQuizRes = await axios
              .get(
                `${process.env.VUE_APP_API}/lesson_quizzes_detail/${c.id}/?Key=${this.userInfo.token_key}&qualify_certificate=True`,
              )
              .catch(() => null);
            if (lessonQuizRes && lessonQuizRes.data?.data) {
              const data = lessonQuizRes.data.data || [];
              const filtered = data.filter(
                (q) => q.quiz?.name && q.quiz.name.toLowerCase().includes(sq),
              );
              filtered.forEach((q) =>
                allLessonQuizzes.push({
                  ...q,
                  courseInfo: c,
                  _status: this.getQuizStatus(q.quiz),
                }),
              );
            }
          }),
        );

        if (
          matchedCourses.length === 0 &&
          allAssignments.length === 0 &&
          allAssessments.length === 0 &&
          allLessonQuizzes.length === 0
        ) {
          this.pushBotReply(
            "I couldn't find any courses, assignments, or assessments matching that query. Try asking something else!",
            "message",
            "general",
          );
        } else {
          this.pushBotReply(
            `Here are the results for "${searchText}":`,
            "global_search_results",
            null,
            {
              courses: matchedCourses,
              assignments: allAssignments,
              assessments: allAssessments,
              lessonQuizzes: allLessonQuizzes,
            },
          );
        }
      } catch (error) {
        console.error("Global search error:", error);
        this.pushBotReply(
          "Sorry, an error occurred while searching. I can only help with questions about your courses.",
          "message",
          "general",
        );
      } finally {
        this.isTyping = false;
      }
    },
  },
};
</script>

<style scoped>
@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes bounceDot {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.6;
  }
  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

/* --- Robot mascot animations --- */
@keyframes robotMascotFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}
.robot-mascot-float {
  animation: robotMascotFloat 3s ease-in-out infinite;
}

@keyframes robot3DTilt {
  0%,
  100% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(10deg);
  }
}
.robot-3d-tilt {
  animation: robot3DTilt 5s ease-in-out infinite;
}

@keyframes botIdleShake {
  0%,
  75%,
  100% {
    transform: rotate(0deg);
  }
  78% {
    transform: rotate(-10deg);
  }
  81% {
    transform: rotate(9deg);
  }
  84% {
    transform: rotate(-8deg);
  }
  87% {
    transform: rotate(7deg);
  }
  90% {
    transform: rotate(-4deg);
  }
  93% {
    transform: rotate(3deg);
  }
}

@keyframes botPop {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.18);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .robot-mascot-float,
  .robot-3d-tilt {
    animation: none;
  }
}

@supports not (height: 100dvh) {
  .h-screen-fallback {
    height: 100vh;
  }
}
</style>
